"use client"

import * as React from "react"
import Box from "@mui/material/Box"
import Typography from "@mui/material/Typography"
import { useTranslation } from "react-i18next"

export interface UpcomingCountdownProps {
  targetDate: string | null | undefined
  label?: string
  compact?: boolean
  variant?: "compact" | "blocks" | "inline"
  size?: "xs" | "sm" | "md"
  colorScheme?: "crimson" | "dark" | "rose"
}

const UpcomingCountdown = ({
  targetDate,
  label,
  compact = false,
  variant,
  size = "md",
  colorScheme = "crimson"
}: UpcomingCountdownProps) => {
  const { t, i18n } = useTranslation()
  const isEn = i18n?.language?.startsWith("en")
  const activeVariant = variant ?? (compact ? "compact" : "blocks")
  const targetMs = targetDate ? new Date(targetDate).getTime() : Number.NaN
  const [remaining, setRemaining] = React.useState<number | null>(null)

  React.useEffect(() => {
    if (!Number.isFinite(targetMs)) {
      setRemaining(null)
      return
    }
    const update = () => setRemaining(Math.max(0, targetMs - Date.now()))
    update()
    const interval = window.setInterval(update, 1000)
    return () => window.clearInterval(interval)
  }, [targetMs])

  if (!Number.isFinite(targetMs)) return null

  if (remaining === 0) {
    return (
      <Typography
        component="span"
        sx={{
          fontSize: size === "xs" ? 10 : size === "sm" ? 11 : 12,
          fontWeight: 700,
          color: "#B42D37",
          whiteSpace: "nowrap"
        }}
      >
        {t("sale_started", { defaultValue: "Đã mở bán" })}
      </Typography>
    )
  }

  const seconds = remaining === null ? null : Math.floor(remaining / 1000)
  const days = seconds === null ? 0 : Math.floor(seconds / 86400)
  const hours = seconds === null ? null : Math.floor((seconds % 86400) / 3600)
  const minutes = seconds === null ? null : Math.floor((seconds % 3600) / 60)
  const secs = seconds === null ? null : seconds % 60
  const pad = (value: number | null) =>
    value === null ? "--" : String(value).padStart(2, "0")

  // Compact variant (inline numbers, ideal for product cards)
  if (activeVariant === "compact") {
    return (
      <Box
        component="span"
        role="timer"
        aria-label={
          seconds === null
            ? t("countdown_loading", { defaultValue: "Đang tải thời gian" })
            : `${label || t("sale_open_after", { defaultValue: "Mở bán sau" })}: ${days > 0 ? `${days}${t("day_short", { defaultValue: isEn ? "d" : "N" })} ` : ""}${hours}h ${minutes}m ${secs}s`
        }
        sx={{
          display: "inline-flex",
          alignItems: "center",
          fontSize:
            size === "xs"
              ? 10.5
              : size === "sm"
                ? { xs: 10.5, md: 12 }
                : { xs: 11.5, md: 13 },
          fontWeight: 700,
          color: "#B42D37",
          textShadow: "0 1px 2px rgba(255, 255, 255, 0.85)",
          lineHeight: 1.2,
          whiteSpace: "nowrap",
          fontVariantNumeric: "tabular-nums",
          letterSpacing: "0.01em"
        }}
      >
        {days > 0 && (
          <Box
            component="span"
            sx={{
              mr: { xs: 0.25, md: 0.35 },
              px: { xs: "2px", md: "3px" },
              py: "1px",
              borderRadius: "2px",
              bgcolor: "#FCEEEF",
              color: "#B42D37",
              fontSize: size === "xs" ? 8.5 : { xs: 8.5, md: 9.5 },
              fontWeight: 700,
              lineHeight: 1
            }}
          >
            {days}{t("day_short", { defaultValue: isEn ? "d" : "N" })}
          </Box>
        )}
        <span>{pad(hours)}</span>
        <Box component="span" sx={{ mx: { xs: "1px", md: "2px" }, opacity: 0.65 }}>
          :
        </Box>
        <span>{pad(minutes)}</span>
        <Box component="span" sx={{ mx: { xs: "1px", md: "2px" }, opacity: 0.65 }}>
          :
        </Box>
        <span>{pad(secs)}</span>
      </Box>
    )
  }

  // Blocks variant (styled digit boxes, ideal for popup modal & detail page)
  const boxStyles = {
    sm: {
      minWidth: 20,
      height: 20,
      fontSize: 11,
      borderRadius: "4px",
      px: 0.4
    },
    md: {
      minWidth: 24,
      height: 24,
      fontSize: 12.5,
      borderRadius: "5px",
      px: 0.5
    },
    xs: {
      minWidth: 18,
      height: 18,
      fontSize: 10,
      borderRadius: "3px",
      px: 0.3
    }
  }[size]

  const digitBg = colorScheme === "dark" ? "#2B2423" : "#B42D37"
  const digitColor = "#FFFFFF"

  return (
    <Box
      sx={{
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "flex-start"
      }}
    >
      {label && (
        <Typography
          sx={{
            fontSize: size === "sm" ? 10 : 10.5,
            fontWeight: 600,
            lineHeight: 1.3,
            color: "#7A6F6D",
            textTransform: "uppercase",
            letterSpacing: "0.03em",
            mb: 0.5
          }}
        >
          {label}
        </Typography>
      )}
      <Box
        role="timer"
        aria-label={
          seconds === null
            ? t("countdown_loading", { defaultValue: "Đang tải thời gian" })
            : `${label || t("sale_open_after", { defaultValue: "Mở bán sau" })}: ${days > 0 ? `${days} ${days === 1 ? t("day_unit", { defaultValue: isEn ? "day" : "ngày" }) : t("days_unit", { defaultValue: isEn ? "days" : "ngày" })} ` : ""}${hours}h ${minutes}m ${secs}s`
        }
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "3px",
          fontVariantNumeric: "tabular-nums"
        }}
      >
        {days > 0 && (
          <>
            <Box
              component="span"
              sx={{
                ...boxStyles,
                minWidth: "auto",
                px: 0.6,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "#FFF0F2",
                color: "#B42D37",
                border: "1px solid #F8CED3",
                fontWeight: 700,
                lineHeight: 1
              }}
            >
              {days} {days === 1 ? t("day_unit", { defaultValue: isEn ? "day" : "ngày" }) : t("days_unit", { defaultValue: isEn ? "days" : "ngày" })}
            </Box>
            <Box
              component="span"
              sx={{ color: "#B42D37", fontWeight: 700, mx: "1px" }}
            >
              ·
            </Box>
          </>
        )}
        <Box
          component="span"
          sx={{
            ...boxStyles,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: digitBg,
            color: digitColor,
            fontWeight: 700,
            lineHeight: 1
          }}
        >
          {pad(hours)}
        </Box>
        <Box
          component="span"
          sx={{
            color: "#B42D37",
            fontWeight: 800,
            fontSize: boxStyles.fontSize,
            lineHeight: 1
          }}
        >
          :
        </Box>
        <Box
          component="span"
          sx={{
            ...boxStyles,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: digitBg,
            color: digitColor,
            fontWeight: 700,
            lineHeight: 1
          }}
        >
          {pad(minutes)}
        </Box>
        <Box
          component="span"
          sx={{
            color: "#B42D37",
            fontWeight: 800,
            fontSize: boxStyles.fontSize,
            lineHeight: 1
          }}
        >
          :
        </Box>
        <Box
          component="span"
          sx={{
            ...boxStyles,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: digitBg,
            color: digitColor,
            fontWeight: 700,
            lineHeight: 1
          }}
        >
          {pad(secs)}
        </Box>
      </Box>
    </Box>
  )
}

export default UpcomingCountdown
